<!-- Floating Health & Integrity Tooltip Container -->
<div
  class="health-tooltip-card fixed z-[1050] bg-white border border-gray-200 rounded-xl shadow-2xl p-4 w-[360px] pointer-events-auto max-h-[88vh] overflow-y-auto"
  [style.display]="showHealthTooltipCard ? 'block' : 'none'" [style.top.px]="healthTooltipPosition.top"
  [style.left.px]="healthTooltipPosition.left" (mouseenter)="healthTooltipHovered = true"
  (mouseleave)="healthTooltipHovered = false; clearHealthTooltip()">

  <!-- Tooltip Header -->
  <div class="flex items-center justify-between !pb-1 !mb-1 border-b border-gray-100">
    <div class="flex items-center gap-2">
      <span class="material-symbols-outlined text-[20px] text-gray-700">shield</span>
      <div>
        <div class="font-semibold text-sm text-gray-900 leading-tight">Client Agent Health</div>
      </div>
    </div>
    <div [ngSwitch]="getUserHealthState()">
      <span *ngSwitchCase="'HEALTHY'"
        class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
        Healthy
      </span>
      <span *ngSwitchCase="'UNHEALTHY'"
        class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
        Unhealthy
      </span>
      <span *ngSwitchCase="'STARTING'"
        class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
        Starting
      </span>
      <span *ngSwitchCase="'LOGOUT'"
        class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
        Logout
      </span>
      <span *ngSwitchCase="'UNINSTALLED'"
        class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 flex items-center gap-1">
        <span class="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
        Uninstalled
      </span>
      <span *ngSwitchDefault class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
        Unknown
      </span>
    </div>
  </div>

  <!-- Last Heartbeat Info -->
  <div class="flex items-center justify-between text-xs text-gray-500 mb-3 px-1 py-1.5">
    <span class="flex items-center gap-1">
      Last Checked :
    </span>
    <span class="font-medium text-gray-700">
      {{ agentIntegrity?.lastHeartbeatAtUtc ? (formatHealthTimestamp(agentIntegrity.lastHeartbeatAtUtc) + ' (' +
      getHealthRelativeTime(agentIntegrity.lastHeartbeatAtUtc) + ')') : 'No heartbeat yet' }}
    </span>
  </div>

  <ng-container *ngIf="getAgentUpdateView() as update">
    <div class="mb-3 p-2.5 rounded-lg border text-xs"
      [class.bg-red-50]="update.tone === 'error'" [class.border-red-200]="update.tone === 'error'"
      [class.bg-amber-50]="update.tone === 'warn'" [class.border-amber-200]="update.tone === 'warn'"
      [class.bg-emerald-50]="update.tone === 'success'" [class.border-emerald-200]="update.tone === 'success'"
      [class.bg-blue-50]="update.tone === 'progress'" [class.border-blue-200]="update.tone === 'progress'">
      <div class="font-semibold text-gray-900">{{ update.label }}</div>
      <div class="mt-0.5 text-gray-600 whitespace-normal" *ngIf="update.detail">{{ update.detail }}</div>
    </div>
  </ng-container>

  <!-- Active Failure Card (Only if latest is Unhealthy) -->
  <div *ngIf="getUserHealthState() === 'UNHEALTHY'" class="mb-3">
    <!-- Unhealthy Status Card -->
    <div class="p-2.5 bg-red-50 border border-red-200 transition-all"
      [ngClass]="isAnyActiveFixExpanded() ? 'rounded-t-lg border-b-0' : 'rounded-lg'">

      <!-- Multi Reason List -->
      <div *ngIf="getHumanReadableReasonList(getLatestHealthEntry()?.reasonCode).length > 1; else singleProfileReason"
        class="space-y-2">
        <div *ngFor="let r of getHumanReadableReasonList(getLatestHealthEntry()?.reasonCode); let rIdx = index"
          class="flex items-start justify-between gap-1.5 text-xs text-red-700 leading-snug">
          <div class="flex items-start gap-1.5 min-w-0 flex-1">
            <span class="text-red-400 mt-0.5 text-[8px] flex-shrink-0">•</span>
            <span class="font-medium whitespace-normal break-words min-w-0 flex-1">{{ r }}</span>
          </div>
          <span *ngIf="!isMenuTooltip && getReasonFix(getReasonCodeByIndex(getLatestHealthEntry()?.reasonCode, rIdx))"
            (click)="toggleFixAccordion('active_' + rIdx); $event.stopPropagation()"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap"
            title="Click to view troubleshooting steps">
            <span class="material-symbols-outlined text-[11px] leading-none">build</span>
            <span>Fix It</span>
          </span>
        </div>
      </div>

      <!-- Single Reason -->
      <ng-template #singleProfileReason>
        <div class="flex items-start justify-between gap-2">
          <div class="flex items-start gap-2 min-w-0 flex-1">
            <span class="material-symbols-outlined text-[18px] text-red-600 flex-shrink-0 mt-0.5">error</span>
            <p class="text-xs text-red-700 leading-snug !m-0 font-medium whitespace-normal break-words min-w-0 flex-1">
              {{ getHumanReadableReason(getLatestHealthEntry()?.reasonCode) }}
            </p>
          </div>
          <span *ngIf="!isMenuTooltip && getReasonFix(getReasonCodeByIndex(getLatestHealthEntry()?.reasonCode, 0))"
            (click)="toggleFixAccordion('active_0'); $event.stopPropagation()"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap mt-0.5"
            title="Click to view troubleshooting steps">
            <span class="material-symbols-outlined text-[11px] leading-none">build</span>
            <span>Fix It</span>
          </span>
        </div>
      </ng-template>
    </div>

    <!-- Accordion: Steps directly under this unhealthy card -->
    <ng-container *ngIf="!isMenuTooltip">
      <ng-container
        *ngIf="getHumanReadableReasonList(getLatestHealthEntry()?.reasonCode).length > 1; else singleProfileAccordionSteps">
        <ng-container
          *ngFor="let r of getHumanReadableReasonList(getLatestHealthEntry()?.reasonCode); let rIdx = index">
          <div *ngIf="isFixExpanded('active_' + rIdx)"
            class="bg-slate-50/50 border border-slate-200 rounded-b-lg p-2.5 shadow-sm -mt-[1px]">
            <div
              class="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-between">
              <span class="flex items-center gap-1">
                <span class="material-symbols-outlined text-[13px] text-[#5459d1]"
                  style="color: #5459d1;">checklist</span>
                <span>Troubleshooting Steps</span>
              </span>
              <span
                class="inline-flex items-center gap-0.5 text-[10px] text-gray-400 font-medium hover:text-gray-700 cursor-pointer transition-colors"
                (click)="toggleFixAccordion('active_' + rIdx); $event.stopPropagation()">
                <span>Close</span>
                <span class="material-symbols-outlined text-[13px] leading-none">expand_less</span>
              </span>
            </div>
            <div class="space-y-1.5">
              <div
                *ngFor="let step of getReasonFixSteps(getReasonCodeByIndex(getLatestHealthEntry()?.reasonCode, rIdx)); let sIdx = index"
                class="flex items-start gap-2 text-xs text-gray-700 leading-snug">
                <span
                  class="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold flex-shrink-0 mt-0.5"
                  style="background-color: rgba(84, 89, 209, 0.12); color: #5459d1;">
                  {{ sIdx + 1 }}
                </span>
                <span class="min-w-0 flex-1 whitespace-normal break-words">{{ step }}</span>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>

      <ng-template #singleProfileAccordionSteps>
        <div *ngIf="isFixExpanded('active_0')"
          class="bg-slate-50/50 border border-slate-200 rounded-b-lg p-2.5 shadow-sm -mt-[1px]">
          <div
            class="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-between">
            <span class="flex items-center gap-1">
              <span class="material-symbols-outlined text-[13px] text-[#5459d1]"
                style="color: #5459d1;">checklist</span>
              <span>Troubleshooting Steps</span>
            </span>
            <span
              class="inline-flex items-center gap-0.5 text-[10px] text-gray-400 font-medium hover:text-gray-700 cursor-pointer transition-colors"
              (click)="toggleFixAccordion('active_0'); $event.stopPropagation()">
              <span>Close</span>
              <span class="material-symbols-outlined text-[13px] leading-none">expand_less</span>
            </span>
          </div>
          <div class="space-y-1.5">
            <div
              *ngFor="let step of getReasonFixSteps(getReasonCodeByIndex(getLatestHealthEntry()?.reasonCode, 0)); let sIdx = index"
              class="flex items-start gap-2 text-xs text-gray-700 leading-snug">
              <span
                class="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold flex-shrink-0 mt-0.5"
                style="background-color: rgba(84, 89, 209, 0.12); color: #5459d1;">
                {{ sIdx + 1 }}
              </span>
              <span class="min-w-0 flex-1 whitespace-normal break-words">{{ step }}</span>
            </div>
          </div>
        </div>
      </ng-template>
    </ng-container>
  </div>

  <!-- History Logs Section -->
  <div>
    <div class="flex items-center justify-between mb-2">
      <span class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Health History</span>
      <span class="text-[11px] text-gray-400 font-normal">
        {{ getHealthHistory().length }} log(s)
      </span>
    </div>

    <div *ngIf="getHealthHistory().length > 0; else noProfileHistory" class="space-y-2 max-h-48 overflow-y-auto pr-1">
      <div *ngFor="let item of getHealthHistory(); let idx = index" class="p-2 rounded-lg border text-xs" [ngClass]="{
             'border-emerald-200 bg-emerald-50/60': getHealthItemState(item) === 'HEALTHY',
             'border-red-200 bg-red-50/60': getHealthItemState(item) === 'UNHEALTHY',
             'border-blue-200 bg-blue-50/60': getHealthItemState(item) === 'STARTING',
             'border-gray-200 bg-gray-50': getHealthItemState(item) !== 'HEALTHY' && getHealthItemState(item) !== 'UNHEALTHY' && getHealthItemState(item) !== 'STARTING'
           }">
        <div class="flex items-center justify-between mb-1">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full" [ngClass]="{
                    'bg-emerald-500': getHealthItemState(item) === 'HEALTHY',
                    'bg-red-500': getHealthItemState(item) === 'UNHEALTHY',
                    'bg-blue-500': getHealthItemState(item) === 'STARTING',
                    'bg-gray-400': getHealthItemState(item) !== 'HEALTHY' && getHealthItemState(item) !== 'UNHEALTHY' && getHealthItemState(item) !== 'STARTING'
                  }"></span>
            <span class="font-semibold text-[11px]" [ngClass]="{
                    'text-emerald-800': getHealthItemState(item) === 'HEALTHY',
                    'text-red-800': getHealthItemState(item) === 'UNHEALTHY',
                    'text-blue-800': getHealthItemState(item) === 'STARTING',
                    'text-gray-700': getHealthItemState(item) !== 'HEALTHY' && getHealthItemState(item) !== 'UNHEALTHY' && getHealthItemState(item) !== 'STARTING'
                  }">
              {{ getHealthItemState(item) }}
            </span>
            <span *ngIf="idx === 0" class="text-[9px] px-1 py-0.2 bg-gray-200 text-gray-600 rounded">Current</span>
          </div>
          <span class="text-[10px] text-gray-500">{{ formatHealthTimestamp(item.timestampUtc) }}</span>
        </div>

        <div *ngIf="getHealthItemState(item) === 'UNHEALTHY'" class="text-[11px] text-red-700 mt-1">
          <!-- Multi-reason in history -->
          <div *ngIf="getHumanReadableReasonList(item.reasonCode).length > 1; else singleProfileItemReason"
            class="space-y-1.5">
            <div *ngFor="let r of getHumanReadableReasonList(item.reasonCode); let rIdx = index" class="space-y-1">
              <div class="flex items-start justify-between gap-1.5">
                <div class="flex items-start gap-1 min-w-0 flex-1">
                  <span class="text-red-400 text-[8px] mt-0.5 flex-shrink-0">•</span>
                  <span class="whitespace-normal break-words min-w-0 flex-1">{{ r }}</span>
                </div>
                <span *ngIf="!isMenuTooltip && getReasonFix(getReasonCodeByIndex(item.reasonCode, rIdx))"
                  (click)="toggleFixAccordion('hist_' + idx + '_' + rIdx); $event.stopPropagation()"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap"
                  title="Click to view troubleshooting steps">
                  <span class="material-symbols-outlined text-[11px] leading-none">build</span>
                  <span>Fix It</span>
                </span>
              </div>
              <!-- Accordion Steps for Multi-Reason in History -->
              <div *ngIf="!isMenuTooltip && isFixExpanded('hist_' + idx + '_' + rIdx)"
                class="bg-slate-50/50 border border-slate-200 rounded-md p-2 mt-1.5 shadow-sm">
                <div
                  class="text-[9px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                  <span class="flex items-center gap-1">
                    <span class="material-symbols-outlined text-[12px] text-[#5459d1]"
                      style="color: #5459d1;">checklist</span>
                    <span>Troubleshooting Steps</span>
                  </span>
                  <span
                    class="inline-flex items-center gap-0.5 text-[9px] text-gray-400 font-medium hover:text-gray-700 cursor-pointer transition-colors"
                    (click)="toggleFixAccordion('hist_' + idx + '_' + rIdx); $event.stopPropagation()">
                    <span>Close</span>
                    <span class="material-symbols-outlined text-[12px] leading-none">expand_less</span>
                  </span>
                </div>
                <div class="space-y-1.5">
                  <div
                    *ngFor="let step of getReasonFixSteps(getReasonCodeByIndex(item.reasonCode, rIdx)); let sIdx = index"
                    class="flex items-start gap-1.5 text-[11px] text-gray-700 leading-snug">
                    <span
                      class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold flex-shrink-0 mt-0.5"
                      style="background-color: rgba(84, 89, 209, 0.12); color: #5459d1;">
                      {{ sIdx + 1 }}
                    </span>
                    <span class="min-w-0 flex-1 whitespace-normal break-words">{{ step }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Single Reason in history -->
          <ng-template #singleProfileItemReason>
            <div>
              <div class="flex items-start justify-between gap-1.5">
                <span class="min-w-0 flex-1 whitespace-normal break-words">{{ getHumanReadableReason(item.reasonCode)
                  }}</span>
                <span *ngIf="!isMenuTooltip && getReasonFix(getReasonCodeByIndex(item.reasonCode, 0))"
                  (click)="toggleFixAccordion('hist_' + idx + '_0'); $event.stopPropagation()"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap"
                  title="Click to view troubleshooting steps">
                  <span class="material-symbols-outlined text-[11px] leading-none">build</span>
                  <span>Fix It</span>
                </span>
              </div>
              <!-- Accordion Steps for Single Reason in History -->
              <div *ngIf="!isMenuTooltip && isFixExpanded('hist_' + idx + '_0')"
                class="bg-slate-50/50 border border-slate-200 rounded-md p-2 mt-1.5 shadow-sm">
                <div
                  class="text-[9px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                  <span class="flex items-center gap-1">
                    <span class="material-symbols-outlined text-[12px] text-[#5459d1]"
                      style="color: #5459d1;">checklist</span>
                    <span>Troubleshooting Steps</span>
                  </span>
                  <span
                    class="inline-flex items-center gap-0.5 text-[9px] text-gray-400 font-medium hover:text-gray-700 cursor-pointer transition-colors"
                    (click)="toggleFixAccordion('hist_' + idx + '_0'); $event.stopPropagation()">
                    <span>Close</span>
                    <span class="material-symbols-outlined text-[12px] leading-none">expand_less</span>
                  </span>
                </div>
                <div class="space-y-1.5">
                  <div
                    *ngFor="let step of getReasonFixSteps(getReasonCodeByIndex(item.reasonCode, 0)); let sIdx = index"
                    class="flex items-start gap-1.5 text-[11px] text-gray-700 leading-snug">
                    <span
                      class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold flex-shrink-0 mt-0.5"
                      style="background-color: rgba(84, 89, 209, 0.12); color: #5459d1;">
                      {{ sIdx + 1 }}
                    </span>
                    <span class="min-w-0 flex-1 whitespace-normal break-words">{{ step }}</span>
                  </div>
                </div>
              </div>
            </div>
          </ng-template>
        </div>
        <div *ngIf="getHealthItemState(item) === 'LOGOUT'" class="text-[11px] text-gray-600 mt-0.5">
          User logged out
        </div>
        <div *ngIf="getHealthItemState(item) === 'HEALTHY'" class="text-[11px] text-emerald-700">
          All system health checks passing
        </div>
        <div *ngIf="getHealthItemState(item) === 'STARTING'" class="text-[11px] text-blue-700">
          Agent client initializing services
        </div>
        <div *ngIf="getHealthItemState(item) === 'UNINSTALLED'" class="text-[11px] text-gray-600">
          Client agent uninstalled
        </div>
      </div>
    </div>

    <ng-template #noProfileHistory>
      <div class="py-4 text-center text-xs text-gray-400 italic bg-gray-50 rounded-lg">
        No integrity history recorded yet
      </div>
    </ng-template>
  </div>
</div>

<div class="container-fluid ">
  <div class="row" id="top_view">
    <div class="col s12">
      <h1>Employee Profile</h1>
    </div>
  </div>
  <div class="row employee-profile">
    <div class="col s12" style="padding: 0">
      <div class="background-img">
        <img src="../../../assets/images/199fd29184c6cff24e3445f849af463e.png" alt="" />
      </div>
    </div>
  </div>
  <div class="profile-row flex flex-col items-center gap-3 px-3 md:flex-row">
    <div class="relative top-[-30px] !h-[60px] md:!h-[80px]">
      <user-profile [user]="userDetails" [isName]="false" [userImageClass]="userImageClass" [sizeAvatar]="90"
        [forUserProfile]="true"></user-profile>
    </div>
    <div class="flex flex-col items-start xl:flex-row xl:items-center justify-between flex-wrap gap-4 w-full pb-3 pt-3">
      <div
        class="w-full flex items-center justify-between flex-wrap gap-3 extra-small:flex-nowrap xl:justify-start xl:w-auto">
        <div class="profile-name-section">
          <div class="user-name-row">
            <h3 class="user-name text-[16px] extra-small:text-[1rem]">{{trimName( userDetails && userDetails.name )}}
            </h3>
            <span *ngIf="showRustAgentBetaBadge" class="agent-beta-badge agent-beta-badge--name"
              [matTooltip]="sysConfig.rustAgentNameBadgeTooltip" matTooltipPosition="below">{{
              sysConfig.rustAgentBetaBadge }}</span>
          </div>
          <h6 class="user-role">{{userDetails?.role | getUserRole}}</h6>
        </div>
        <div class="status-maintain flex flex-row items-center gap-2 flex-wrap" *ngIf="presenceResolved">
          <div [ngSwitch]="getProfileStatusType()"
            class="status-container profile-status-pills inline-flex items-center">
            <div *ngSwitchCase="'auto-logout'" class="status-wrapper">
              <div [matTooltip]="getProfileAutoLogoutTooltip()"
                class="auto-logout-status online-offline-status-color-auto-logout">
                Auto Logout
              </div>
            </div>
            <div *ngSwitchCase="'idle'" class="status-wrapper status_block">
              <div class="online-offline-status online-offline-status-color-idle"
                [matTooltip]="getProfileIdleTooltip()">
                Idle
              </div>
            </div>
            <div *ngSwitchCase="'offline'" class="status-wrapper">
              <div class="online-offline-status online-offline-status-color-offline"
                [matTooltip]="getProfileOfflineTooltip()">
                offline
              </div>
            </div>
            <div *ngSwitchCase="'absent'" class="status-wrapper">
              <div class="online-offline-status online-offline-status-color-absent"
                [matTooltip]="getProfileOfflineTooltip()">
                Absent
              </div>
            </div>
            <div *ngSwitchCase="'online'" class="status-wrapper status_block">
              <div class="online-offline-status online-offline-status-color-online">
                online
              </div>
            </div>
            <div *ngSwitchCase="'invited'" class="status-wrapper status_block">
              <div class="online-offline-status online-offline-status-color-away">
                Invited
              </div>
            </div>
            <span *ngSwitchDefault></span>
          </div>

          <!-- Health Status Badge -->
          <div [ngSwitch]="getUserHealthState()" class="health-wrapper">
            <!-- Healthy Status -->
            <!-- <div *ngSwitchCase="'HEALTHY'">
              <div class="health-status-badge health-status-healthy" (mouseenter)="showHealthTooltip($event)"
                (mouseleave)="hideHealthTooltip()">
                <span class="health-dot health-dot-healthy"></span>
                <span>Healthy</span>
              </div>
            </div> -->

            <!-- Unhealthy Status -->
            <div *ngSwitchCase="'UNHEALTHY'">
              <div class="health-status-badge health-status-unhealthy" (mouseenter)="showHealthTooltip($event)"
                (mouseleave)="hideHealthTooltip()">
                <span class="health-dot health-dot-unhealthy animate-pulse"></span>
                <span>Unhealthy</span>
              </div>
            </div>

            <!-- Starting Status -->
            <!-- <div *ngSwitchCase="'STARTING'">
              <div class="health-status-badge health-status-starting" (mouseenter)="showHealthTooltip($event)"
                (mouseleave)="hideHealthTooltip()">
                <span class="health-dot health-dot-starting"></span>
                <span>Starting</span>
              </div>
            </div> -->

            <!-- Logout Status -->
            <div *ngSwitchCase="'LOGOUT'">
              <div class="health-status-badge health-status-neutral" (mouseenter)="showHealthTooltip($event)"
                (mouseleave)="hideHealthTooltip()">
                <span class="health-dot health-dot-neutral"></span>
                <span>Logout</span>
              </div>
            </div>

            <!-- Uninstalled Status -->
            <div *ngSwitchCase="'UNINSTALLED'">
              <div class="health-status-badge health-status-neutral" (mouseenter)="showHealthTooltip($event)"
                (mouseleave)="hideHealthTooltip()">
                <span class="health-dot health-dot-neutral"></span>
                <span>Uninstalled</span>
              </div>
            </div>

            <!-- Unknown / Not available -->
            <!-- <div *ngSwitchDefault>
              <div class="health-status-badge health-status-neutral"
                *ngIf="getHealthHistory().length; else emptyHealthProfile" (mouseenter)="showHealthTooltip($event)"
                (mouseleave)="hideHealthTooltip()">
                <span class="health-dot health-dot-neutral"></span>
                <span>N/A</span>
              </div>
              <ng-template #emptyHealthProfile>
                <div class="health-status-badge health-status-neutral cursor-default">
                  <span>--</span>
                </div>
              </ng-template>
            </div> -->
          </div>
          <div class="signed-out-at" *ngIf="getSignedOutLabel()">{{ getSignedOutLabel() }}</div>
          <div class="signed-out-at" *ngIf="getPasswordResetLabel()">{{ getPasswordResetLabel() }}</div>
        </div>
      </div>
      <div class="profile-row-btn-container w-full xl:w-auto">
        <!-- Action Buttons Row -->
        <div class="flex flex-row items-center justify-center extra-small:justify-start gap-3 w-full">
          <!-- Screencast Request Button - Primary Filled Pill -->
          <button matTooltip="Screencast"
            class="!flex !items-center !justify-center !w-10 !h-10 !rounded-lg bg-white hover:!bg-gray-100 !transition-colors !min-w-0 !p-0 !border-0 cursor-pointer"
            [disabled]="!isProfilePresenceActionEnabled()" *ngIf="getProfileStatusType() !== 'invited'"
            (click)="isProfilePresenceActionEnabled() && openScreencastActionModel()">
            <span class="material-symbols-outlined !text-[22px]">cast</span>
          </button>

          <!-- Screenshot Request Button - Soft Secondary Pill -->
          <button matTooltip="Screenshot"
            class="!flex !items-center !justify-center !w-10 !h-10 !rounded-lg bg-white hover:!bg-gray-100 !transition-colors !min-w-0 !p-0 !border-0 cursor-pointer"
            [disabled]="!isProfilePresenceActionEnabled()" *ngIf="getProfileStatusType() !== 'invited'"
            (click)="isProfilePresenceActionEnabled() && screenshotRequest()">
            <span class="material-symbols-outlined !text-[22px]">photo_camera</span>
          </button>

          <!-- Vertical Divider -->
          <div class="h-8 w-px bg-gray-300 mx-1"></div>

          <div class="flex gap-1">
            <!-- Static Download Icon -->
            <div class="flex items-center justify-center w-10 h-10">
              <!-- download agents -->
              <button [matMenuTriggerFor]="downloadMenu" matTooltip="Download Client Agent"
                class="!flex !items-center !justify-center !w-10 !h-10 !rounded-lg bg-white hover:!bg-gray-100 !transition-colors !min-w-0 !p-0 !border-0 cursor-pointer">
                <span class="material-symbols-outlined !text-gray-600 !text-2xl">download</span>
              </button>
              <mat-menu #downloadMenu="matMenu" xPosition="before">
                <button mat-menu-item *ngIf="!isDownloadSoftwareTypeLocked('silent')"
                  [matMenuTriggerFor]="invisibleTracking">
                  Hidden
                  <i class="menu-info-icon material-icons !text-[16px] relative !top-[4px]"
                    [matTooltip]="invisibleTrackingTooltip" matTooltipPosition="after">info_outline</i>
                </button>
                <button mat-menu-item *ngIf="isDownloadSoftwareTypeLocked('silent')" class="download-menu-item--locked"
                  (click)="onLockedDownloadSoftwareTypeClick('silent')">
                  Hidden
                  <mat-icon class="download-menu-lock" aria-hidden="true">lock</mat-icon>
                  <i class="menu-info-icon material-icons !text-[16px] relative !top-[4px]"
                    [matTooltip]="invisibleTrackingTooltip" matTooltipPosition="after">info_outline</i>
                </button>
                <button mat-menu-item [matMenuTriggerFor]="selfControl">
                  Employee Controlled
                  <i class="menu-info-icon material-icons !text-[16px] relative !top-[4px]"
                    [matTooltip]="selfControlTooltip" matTooltipPosition="after">info_outline</i>
                </button>
                <button mat-menu-item *ngIf="!isDownloadSoftwareTypeLocked('restricted')"
                  [matMenuTriggerFor]="adminControl">
                  Admin Controlled
                  <i class="menu-info-icon material-icons !text-[16px] relative !top-[4px]"
                    [matTooltip]="adminControlTooltip" matTooltipPosition="after">info_outline</i>
                </button>
                <button mat-menu-item *ngIf="isDownloadSoftwareTypeLocked('restricted')"
                  class="download-menu-item--locked" (click)="onLockedDownloadSoftwareTypeClick('restricted')">
                  Admin Controlled
                  <mat-icon class="download-menu-lock" aria-hidden="true">lock</mat-icon>
                  <i class="menu-info-icon material-icons !text-[16px] relative !top-[4px]"
                    [matTooltip]="adminControlTooltip" matTooltipPosition="after">info_outline</i>
                </button>
              </mat-menu>
              <mat-menu #selfControl="matMenu" xPosition="before">
                <button mat-menu-item (click)="downloadRustGeneralAgent()">
                  Windows 10 & 11
                </button>
                <button mat-menu-item *ngFor="let item of generalDownloads" (click)="downloadGeneralAgent(item.label)">
                  {{ item.label }}
                </button>
              </mat-menu>

              <!-- Invisible Tracking Menu -->
              <mat-menu #invisibleTracking="matMenu" xPosition="before">
                <button mat-menu-item (click)="autoResetPasswordRust('Windows', userDetails)">
                  Windows 10 & 11
                </button>
                <button mat-menu-item *ngFor="let item of silentDownloads"
                  (click)="autoResetPassword(item.label, userDetails)">
                  {{ item.label }}
                </button>
              </mat-menu>

              <!-- Admin Control Menu -->
              <mat-menu #adminControl="matMenu" xPosition="before">
                <button mat-menu-item (click)="downloadRustAdminControlAgent('Windows', userDetails)">
                  Windows 10 & 11
                </button>
                <button mat-menu-item *ngFor="let item of generalDownloads"
                  (click)="downloadAdminControlAgent(item.label, userDetails)">
                  {{ item.label }}
                </button>
              </mat-menu>
            </div>

            <!-- More Menu Button - Three Dots -->
            <button *ngIf="checkIsPersonalize" matTooltip="Personalize Settings"
              [routerLink]="['/employee/setting/', userDetails.id]"
              class="!flex !items-center !justify-center !w-10 !h-10 !rounded-lg bg-white hover:!bg-gray-100 !transition-colors !min-w-0 !p-0 !border-0 cursor-pointer">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.5 14.4098C13.5 13.8598 13.95 13.4098 14.5 13.4098C15.05 13.4098 15.5 13.8598 15.5 14.4098C15.5 14.9598 15.05 15.4098 14.5 15.4098C13.95 15.4098 13.5 14.9598 13.5 14.4098ZM11.5 14.4098C11.5 16.0698 12.84 17.4098 14.5 17.4098C16.16 17.4098 17.5 16.0698 17.5 14.4098C17.5 12.7498 16.16 11.4098 14.5 11.4098C12.84 11.4098 11.5 12.7498 11.5 14.4098ZM9.5 5.90984L7.09 4.81984L6 2.40984L4.91 4.81984L2.5 5.90984L4.91 6.99984L6 9.40984L7.09 6.99984L9.5 5.90984ZM13.22 3.12984L11.5 3.90984L13.22 4.68984L14 6.40984L14.78 4.68984L16.5 3.90984L14.78 3.12984L14 1.40984L13.22 3.12984ZM14.5 8.15984C14.38 8.15984 14.25 8.15984 14.13 8.16984L12.66 6.22984L8.33 8.72984L9.27 10.9698C9.14 11.1698 9.01 11.3898 8.9 11.6098L6.5 11.9098L6.5 16.9098L8.91 17.2098C9.02 17.4298 9.15 17.6398 9.28 17.8498L8.33 20.0898L12.66 22.5898L14.13 20.6498C14.25 20.6598 14.38 20.6598 14.5 20.6598C14.62 20.6598 14.75 20.6598 14.87 20.6498L16.34 22.5898L20.67 20.0898L19.73 17.8498C19.86 17.6498 19.99 17.4298 20.1 17.2098L22.5 16.9098L22.5 11.9098L20.09 11.6098C19.98 11.3898 19.86 11.1798 19.72 10.9698L20.66 8.72984L16.33 6.22984L14.86 8.16984C14.75 8.15984 14.62 8.15984 14.5 8.15984ZM18.14 9.57984L17.41 11.3098C18.01 11.8698 18.45 12.6098 18.64 13.4398L20.5 13.6798L20.5 15.1398L18.64 15.3698C18.45 16.1998 18.01 16.9398 17.41 17.4998L18.14 19.2298L16.87 19.9598L15.74 18.4698C15.35 18.5898 14.94 18.6498 14.51 18.6498C14.08 18.6498 13.67 18.5898 13.28 18.4698L12.15 19.9598L10.88 19.2298L11.61 17.4998C11.01 16.9398 10.57 16.1998 10.38 15.3698L8.5 15.1398L8.5 13.6698L10.36 13.4398C10.55 12.6098 10.99 11.8698 11.59 11.3098L10.86 9.57984L12.13 8.84984L13.26 10.3398C13.65 10.2198 14.06 10.1598 14.49 10.1598C14.92 10.1598 15.33 10.2198 15.72 10.3398L16.85 8.84984L18.14 9.57984Z"
                  fill="#7478D8" />
              </svg>
            </button>

            <!-- Warning icon - version is 4.1.6 (YELLOW) -->
            <button [matMenuTriggerFor]="systemInformation" *ngIf="isWarningVersion(currentApplicationInformation)"
              [matTooltip]="getVersionWarningTooltip(currentApplicationInformation)"
              class="!flex !items-center !justify-center !w-10 !h-10 !rounded-lg bg-white hover:!bg-gray-100 !transition-colors !min-w-0 !p-0 !border-0 cursor-pointer">
              <span class="material-symbols-outlined !text-yellow-600 !text-2xl">warning</span>
            </button>

            <button [matMenuTriggerFor]="systemInformation" *ngIf="isCriticallyOutdated(currentApplicationInformation)"
              [matTooltip]="getVersionWarningTooltip(currentApplicationInformation)"
              class="!flex !items-center !justify-center !w-10 !h-10 !rounded-lg bg-white hover:!bg-gray-100 !transition-colors !min-w-0 !p-0 !border-0 cursor-pointer">
              <span class="material-symbols-outlined !text-red-600 !text-2xl">cancel</span>
            </button>

            <!-- More Menu Button - Three Dots -->
            <button [matMenuTriggerFor]="menu"
              class="!flex !items-center !justify-center !w-10 !h-10 !rounded-lg bg-white hover:!bg-gray-100 !transition-colors !min-w-0 !p-0 !border-0 cursor-pointer">
              <span class="material-symbols-outlined !text-gray-600 !text-2xl">more_vert</span>
            </button>
          </div>

        </div>
        <!-- Menu Dropdown (unchanged functionality) -->
        <mat-menu #menu="matMenu" (closed)="clearHealthTooltip()">
          <div *ngFor="let item of menuItems" class="more-button-dropdowns">
            <div class="row dropdown-hover"
              *ngIf="isAdmin ? item.roles.includes('isAdmin'):isManager ? item.roles.includes('isManager'):isTeamLead ? item.roles.includes('isTeamLead'):false">
              <div class="col s12" *ngIf="!item.isSubmenu">
                <p *ngIf="!item.isSvg" [ngClass]="item.class" (click)="item.action()">
                  <span [ngClass]="'online'" class="material-symbols-outlined">{{ item.icon }}</span>{{ item.label }}
                </p>
                <p *ngIf="item.isSvg" [ngClass]="item.class" (click)="item.action()">
                  <span class="material-symbols-outlined" [innerHTML]="item.icon"
                    [matTooltip]="item?.matTooltip"></span>{{ item.label }}
                </p>
              </div>
              <ng-container *ngIf="item.isSubmenu">
                <div mat-menu-item [matMenuTriggerFor]="systemInformation" class="col s12">
                  <p [class]="item.class" [ngClass]="{
                    'text-red-600 font-medium': item.id == 'appInfo' && !isOutdatedVersion(currentApplicationInformation) && !isWarningVersion(currentApplicationInformation) && !isCriticallyOutdated(currentApplicationInformation) && (getUserHealthState() == 'UNHEALTHY' || getUserHealthState() == 'UNINSTALLED' || getUserHealthState() == 'LOGOUT')
                  }">
                    <!-- Critical ban icon - version is below 4.1.6 (RED) -->
                    <span class="material-symbols-outlined online"
                      *ngIf="item.id == 'appInfo' && isCriticallyOutdated(currentApplicationInformation)"
                      [matTooltip]="getVersionWarningTooltip(currentApplicationInformation)">
                      cancel
                    </span>

                    <!-- Warning icon - version is 4.1.6 (YELLOW) -->
                    <span class="material-symbols-outlined online"
                      *ngIf="item.id == 'appInfo' && isWarningVersion(currentApplicationInformation)"
                      [matTooltip]="getVersionWarningTooltip(currentApplicationInformation)">
                      warning
                    </span>

                    <span class="material-symbols-outlined online"
                      *ngIf="item.id != 'appInfo' || (!isOutdatedVersion(currentApplicationInformation) && !(getUserHealthState() == 'UNHEALTHY' || getUserHealthState() == 'UNINSTALLED' || getUserHealthState() == 'LOGOUT'))"
                      matTooltip="Desktop Agent Information">
                      {{ item.icon }}
                    </span>

                    <span class="material-symbols-outlined online animate-pulse transition-all font-semibold"
                      *ngIf="item.id == 'appInfo' && !isOutdatedVersion(currentApplicationInformation) && !isWarningVersion(currentApplicationInformation) && !isCriticallyOutdated(currentApplicationInformation) && (getUserHealthState() == 'UNHEALTHY' || getUserHealthState() == 'UNINSTALLED' || getUserHealthState() == 'LOGOUT')"
                      matTooltip="Desktop Agent Information">
                      error
                    </span>
                    {{ item.label }}
                  </p>
                </div>
              </ng-container>
            </div>
          </div>
        </mat-menu>
        <mat-menu #systemInformation="matMenu" xPosition="before" (closed)="clearHealthTooltip()">
          <ng-container *ngFor="let item of menuItems">
            <ng-container *ngIf="item.isSubmenu">
              <button mat-menu-item *ngFor="let menu of item.submenu.menus" [matTooltip]="menu.tooltip"
                matTooltipPosition="before" class="w-full more-button-dropdowns" (mouseenter)="clearHealthTooltip()">
                <!-- If icon is normal material icon -->
                <ng-container *ngIf="!menu.image">
                  <span class="material-symbols-outlined icon-dropdown">
                    {{ menu.icon }}
                  </span>
                </ng-container>
                <!-- If icon is image -->
                <ng-container *ngIf="menu.image">
                  <img [src]="menu.icon" width="20" height="20" class="icon-dropdown" sty />
                </ng-container>
                <span class="info-text">{{ menu.value }}</span>
              </button>

              <!-- Agent Health Info -->
              <button mat-menu-item
                *ngIf="getUserHealthState() !== 'UNKNOWN' || currentApplicationInformation?.version || agentIntegrity || userDetails?.agentIntegrity"
                class="w-full more-button-dropdowns cursor-pointer" (mouseenter)="showHealthTooltip($event)"
                (mouseleave)="hideHealthTooltip()">
                <span class="material-symbols-outlined icon-dropdown"
                  [ngClass]="[getHealthStatusColorClass(), getUserHealthState() == 'UNHEALTHY' ? 'animate-pulse' : '']">
                  {{ getHealthStatusIcon() }}
                </span>
                <span class="info-text font-medium" [ngClass]="getHealthStatusColorClass()">{{ getHealthStatusLabel()
                  }}</span>
              </button>
              <button mat-menu-item *ngIf="getAgentUpdateView() as update" class="w-full more-button-dropdowns"
                (mouseenter)="clearHealthTooltip()">
                <span class="material-symbols-outlined icon-dropdown">system_update_alt</span>
                <span class="info-text">{{ update.label }}</span>
              </button>
            </ng-container>
          </ng-container>
        </mat-menu>
      </div>
    </div>
  </div>

  <!-- Laptop: stats|habits, insights, apps|websites. Large screens: stats|habits|apps, then insights, then websites. -->
  <div class="profile-overview">
    <div class="statistics-container">
      <mat-card class="workin-card">
        <div class="barHeader">
          <div class="">
            <mat-card-title class="chartTitle">Statistics</mat-card-title>
          </div>
          <div class="displayWeek">
            <i class="material-icons" (click)="!_isUsageLoading && WeekPrevNextAction(true,'Statistics')"
              [class.disabled]="_isUsageLoading"
              [style.cursor]="_isUsageLoading ? 'not-allowed' : 'pointer'">keyboard_arrow_left</i>
            <p class="statics-current-week">{{currentWeekDisplay}}</p>
            <i class="material-icons" (click)="!_isUsageLoading && WeekPrevNextAction(false,'Statistics')"
              [class.disabled]="_isUsageLoading"
              [style.cursor]="_isUsageLoading ? 'not-allowed' : 'pointer'">keyboard_arrow_right</i>
          </div>
        </div>

        <mat-card-content>
          <div *ngIf="_isUsageLoading">
            <ngx-skeleton-loader style="margin: 20px 0px; width: 100%"
              [theme]="{ 'border-radius': '2', height: '260px', margin: '5px' }"></ngx-skeleton-loader>
          </div>
          <div *ngIf="!_isUsageLoading" class="work-hours-bar-chart">
            <div class="barLabel">
              <div style="display: inline-flex;align-items: center;">
                <div class="bar-label-dots working">
                </div>
                <div class="hours-name">
                  Working Hours
                </div>
              </div>
              <div style="display: inline-flex;align-items: center;">
                <div class="bar-label-dots idle">
                </div>
                <div class="hours-name">
                  Idle Hours
                </div>
              </div>
              <div style="display: inline-flex;align-items: center;">
                <div class="bar-label-dots offline">
                </div>
                <div class="hours-name">
                  Offline Hours
                </div>
              </div>
            </div>
            <div class="bar-chart">
              <app-stacked-chart [totalWorkingArray]="totalWorkingHours" [data]="sessionData"
                [weekArr]="currentWeekArray" [labels]="barChartLabels" [type]="'bar'"
                [sessionData]="barData"></app-stacked-chart>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
    <div class="work-habits-container">
      <app-work-habits-card [userId]="userId"></app-work-habits-card>
    </div>

    <app-work-insights-section class="work-insights-slot" [userId]="userId"></app-work-insights-section>

    <div class="application-usage-container">
      <mat-card class="workin-card">
        <div class="donutHeader">
          <div class="row most-used-container">
            <div class="col s12 most-used-contain">
              <div class="chartTitle">Most Used Application</div>
            </div>
            <div class="col s12 most-used-daterange-contain">
              <mat-form-field class="date-range-picker-form-field">
                <mat-date-range-input [rangePicker]="picker" [min]="minApplicationUsageDate"
                  [max]="maxApplicationUsageDate">
                  <input matStartDate [value]="startDate" placeholder="Start date"
                    (dateChange)="onStartDateChange($event)" />
                  <input matEndDate [value]="endDate" placeholder="End date" (dateChange)="onEndDateChange($event)" />
                </mat-date-range-input>
                <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-date-range-picker #picker></mat-date-range-picker>
              </mat-form-field>
            </div>
          </div>
        </div>
        <mat-card-content class="most-used-card-content">
          <div *ngIf="_isAppUsageLoading">
            <ngx-skeleton-loader style="margin: 20px 0px; width: 100%"
              [theme]="{ 'border-radius': '2', height: '260px', margin: '5px' }"></ngx-skeleton-loader>
          </div>
          <div class="application-usage-chart"
            [ngStyle]="{ 'visibility' : _isAppUsageLoading ? 'hidden' : 'visible', 'height' : '100%'}">
            <div class="application-usage-chart-pie">
              <application-usage [isChartWebBrowserUser]="false"></application-usage>
            </div>
            <div class="progressbar-contain-applicationUsage">
              <progressbar [grid]="1" [isDataWebsiteUsage]="false" style="width: 100%;"></progressbar>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <div class="website-usage-container">
      <mat-card class="workin-card">
        <div class="donutHeader">
          <div class="row most-used-container">
            <div class="col s12 most-used-contain">
              <div class="chartTitle">Most Used Websites
                <span style="margin-left: 2px;" class="beta-badge" *ngIf="sysConfig.isBeta"
                  [matTooltip]="sysConfig.betaTooltip">
                  <i>Beta</i>
                </span>
              </div>
            </div>
            <div class="col s12 most-used-daterange-contain">
              <mat-form-field class="date-range-picker-form-field">
                <mat-date-range-input [rangePicker]="pickerWebsiteUsage" [min]="minWebsiteUsageDate"
                  [max]="maxWebsiteUsageDate">
                  <input matStartDate [value]="startDateForWebUsageChart" placeholder="Start date"
                    (dateChange)="onStartDateChangeWebsiteChart($event)" />
                  <input matEndDate [value]="endDateForWebUsageChart" placeholder="End date"
                    (dateChange)="onEndDateChangeWebsiteChart($event)" />
                </mat-date-range-input>
                <mat-datepicker-toggle matIconSuffix [for]="pickerWebsiteUsage"></mat-datepicker-toggle>
                <mat-date-range-picker #pickerWebsiteUsage></mat-date-range-picker>
              </mat-form-field>
            </div>
          </div>
        </div>
        <mat-card-content class="most-used-card-content">
          <div *ngIf="_isWebSiteUsageLoading">
            <ngx-skeleton-loader style="margin: 20px 0px; width: 100%"
              [theme]="{ 'border-radius': '2', height: '260px', margin: '5px' }"></ngx-skeleton-loader>
          </div>
          <div class="website-usage-chart-section" [ngStyle]="{'visibility':_isWebSiteUsageLoading?'hidden':'visible'}">
            <div class="website-usage-chart-canvas">
              <application-usage [isDashboard]="false" [isChartWebBrowserUser]="true"></application-usage>
            </div>
            <div class="progressbar-contain-websiteUsage">
              <progressbar [grid]="1" [isDataWebsiteUsage]="true" style="width: 100%;">
              </progressbar>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <div class="row timeline">
    <div class="col s12">
      <mat-card class="employyee-work-timeline">
        <div class="barHeader">
          <div class="" style="margin-right: 1rem;flex-grow: 1;">
            <mat-card-title class="chartTitle">Employee Work Timeline </mat-card-title>
          </div>
          <div class="displayWeek" style="align-items: center;display: flex;">
            <i class="material-icons" (click)="!_isStackbarLoading && WeekPrevNextAction(true,'Timeline')"
              [class.disabled]="_isStackbarLoading"
              [style.cursor]="_isStackbarLoading ? 'not-allowed' : 'pointer'">keyboard_arrow_left</i>{{stackcurrentWeekDisplay}}
            <i class="material-icons" (click)="!_isStackbarLoading && WeekPrevNextAction(false,'Timeline')"
              [class.disabled]="_isStackbarLoading"
              [style.cursor]="_isStackbarLoading ? 'not-allowed' : 'pointer'">keyboard_arrow_right</i>
          </div>
        </div>

        <mat-card-content>
          <div *ngIf="_isStackbarLoading">
            <ngx-skeleton-loader style="margin: 20px 0px; width: 100%;"
              [theme]="{'border-radius' : '2' , 'height' : '400px', 'margin' : '5px'}"></ngx-skeleton-loader>
          </div>
          <div *ngIf="!_isStackbarLoading">
            <app-stacked-chart [type]="'horizontal'" [data]="stackedChartData" [minValue]="x_min" [maxValue]="x_max"
              [labels]="stackedChartlabel" [tempDataTooltip]="tempDataTooltip"
              [newSessionData]="newSessionTimeline"
              [conductTimelogs]="conductTimelogs"></app-stacked-chart>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>

  <!-- Conduct Task Time Logs Section for Individual Employee (Weekly Date-Grouped View) -->
  <div class="row" *ngIf="isConductIntegrated" style="margin-top: 28px; margin-bottom: 20px;">
    <div class="col s12">
      <mat-card class="workin-card"
        style="border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); padding: 20px;">
        <div
          style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span
              style="display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; background-color: rgba(84, 89, 209, 0.1); color: #5459d1; flex-shrink: 0;">
              <mat-icon style="font-size: 20px; width: 20px; height: 20px;">schedule</mat-icon>
            </span>
            <div class="flex flex-col">
              <div class="flex items-center gap-2 flex-wrap">
                <mat-card-title class="chartTitle" style="margin: 0;">Conduct Task Time Logs</mat-card-title>
                <span *ngIf="conductTimelogs && conductTimelogs.length > 0"
                  style="background: rgba(84, 89, 209, 0.1); color: #5459d1; font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 9999px;">
                  {{ conductTimelogs.length }} {{ conductTimelogs.length === 1 ? 'log' : 'logs' }} across {{
                  groupedConductDateLogs.length }} {{ groupedConductDateLogs.length === 1 ? 'day' : 'days' }}
                </span>
              </div>
              <span class="text-xs text-slate-500 font-medium mt-0">
                Week Total: <span class="font-bold text-[#5459d1]">{{ totalConductWorkedHours }}</span>
              </span>
            </div>
          </div>

          <div style="display: flex; align-items: center; gap: 16px; flex-wrap: wrap;">
            <div class="displayWeek" style="align-items: center; display: flex;">
              <i class="material-icons" (click)="!isImportingConductTimelogs && onConductPreviousWeek()"
                [class.disabled]="isImportingConductTimelogs"
                [style.cursor]="isImportingConductTimelogs ? 'not-allowed' : 'pointer'">keyboard_arrow_left</i>{{conductWeekDisplay}}
              <i class="material-icons" (click)="!isImportingConductTimelogs && onConductNextWeek()"
                [class.disabled]="isImportingConductTimelogs"
                [style.cursor]="isImportingConductTimelogs ? 'not-allowed' : 'pointer'">keyboard_arrow_right</i>
            </div>

            <button mat-stroked-button color="primary" (click)="loadConductTimelogs(true)"
              [disabled]="isImportingConductTimelogs"
              class="flex items-center justify-center font-medium !text-[#5459d1] !border-none bg-none outline-none">
              <mat-spinner *ngIf="isImportingConductTimelogs" diameter="18"></mat-spinner>
              <mat-icon *ngIf="!isImportingConductTimelogs" class="!text-lg !w-5 !h-5 leading-none">sync</mat-icon>
            </button>
          </div>
        </div>

        <!-- Weekly Timelogs Table Grouped by Date -->
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-sm">
            <thead>
              <tr
                class="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                <th class="py-3 px-4">Date</th>
                <th class="py-3 px-4">Projects</th>
                <th class="py-3 px-4">Logs Count</th>
                <th class="py-3 px-4">Total Hours Worked</th>
                <th class="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr *ngFor="let group of groupedConductDateLogs; let i = index" (click)="openConductDrawer(group, i)"
                class="cursor-pointer bg-white hover:bg-slate-50 transition-colors group">
                <td class="py-3.5 px-4 font-semibold text-slate-900">
                  <div class="flex items-center gap-3">
                    <span
                      class="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-50 text-[#5459d1] flex-shrink-0">
                      <mat-icon class="!text-lg !w-4.5 !h-4.5 leading-none">calendar_today</mat-icon>
                    </span>
                    <div class="flex flex-col min-w-0">
                      <span
                        class="font-bold text-slate-900 text-sm truncate group-hover:text-[#5459d1] transition-colors">
                        {{ group.date_display }}
                      </span>
                      <span class="text-xs text-slate-500 font-normal truncate">
                        {{ group.day_label }}<span *ngIf="group.is_today"
                          class="ml-1 text-[#5459d1] font-semibold">(Today)</span>
                      </span>
                    </div>
                  </div>
                </td>
                <td class="py-3.5 px-4">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span *ngFor="let proj of group.projects"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                      {{ proj }}
                    </span>
                    <span *ngIf="!group.projects || group.projects.length === 0" class="text-xs text-slate-400 italic">
                      General
                    </span>
                  </div>
                </td>
                <td class="py-3.5 px-4">
                  <span
                    class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-[#5459d1] border border-indigo-100">
                    {{ group.logs.length }} {{ group.logs.length === 1 ? 'Log' : 'Logs' }}
                  </span>
                </td>
                <td class="py-3.5 px-4">
                  <span
                    class="inline-flex items-center px-3 py-1 rounded-md text-xs font-mono font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                    {{ group.total_worked_hours }}
                  </span>
                </td>
                <td class="py-3 px-4 text-right">
                  <button type="button" (click)="openConductDrawer(group, i); $event.stopPropagation()"
                    class="!h-8 !px-3 !text-xs !font-semibold rounded-lg inline-flex items-center gap-1.5 border border-[#5459d1] text-[#5459d1] bg-white hover:bg-[#5459d1] hover:text-white shadow-xs transition-all duration-150 cursor-pointer">
                    <span>View Timelog</span>
                    <mat-icon class="!text-sm !w-4 !h-4 leading-none">arrow_forward</mat-icon>
                  </button>
                </td>
              </tr>
              <tr
                *ngIf="isImportingConductTimelogs && (!groupedConductDateLogs || groupedConductDateLogs.length === 0)">
                <td colspan="5" class="p-6">
                  <ngx-skeleton-loader count="3"
                    [theme]="{ 'border-radius': '6px', height: '36px', 'margin-bottom': '8px' }"></ngx-skeleton-loader>
                </td>
              </tr>
              <tr
                *ngIf="!isImportingConductTimelogs && (!groupedConductDateLogs || groupedConductDateLogs.length === 0)">
                <td colspan="5" class="p-8 text-center text-slate-400 italic text-sm">
                  No Conduct task timelogs found for this week.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </mat-card>
    </div>
  </div>

  <!-- Conduct Timelogs Detail Drawer now hosted via centralized App Shared Drawer -->

  <div class="row">
    <div class="col s12" style="margin-top: 28px; margin-bottom: 20px">
      <app-screen-shot-viwer [userDetails]="userDetails" [user]="userId" [status]="status"
        (childEvent)="addName($event)" [startDate]="sendingStartDate"
        [endDate]="sendingEndDate"></app-screen-shot-viwer>
    </div>
  </div>
</div>

<div #userModal class="modal">
  <app-create-user [isEditable]="{id: userId, isEdit: true}"></app-create-user>
</div>